/**
 * Return-url host allowlist.
 *
 * Every cross-app transition hands us a `ret` url in the hash payload and we
 * redirect to it with a live Firebase custom token in the fragment. The url is
 * supplied by the calling app, so an app pointed at a host we do not run sends
 * a working token to a page that cannot consume it - the user sees a 404 and
 * the token is left sitting in a third party's url.
 *
 * Hosts are matched on a dot boundary so `wonderschool.com.example.com` does
 * not pass as `wonderschool.com`.
 */

// Hosts we serve that never appear in `wsConfig`: the marketplace white-label
// domains (fldoe/idec/brighthorizons/nm/...) and the partner-branded finders,
// both of which are Nova ingress hosts rather than accounts config values.
const STATIC_ALLOWED_DOMAINS = [
  'wonderschool.com',
  'wonderschool.io',
  'wonderschool.dev',
  'theearlylearningmarketplace.org',
  'ececd.nm.gov',
];

// Any url can be parsed against a base, which also resolves the
// protocol-relative (`//host/path`) form the wsConfig domains use.
const PARSE_BASE = 'https://invalid.localdomain';

/**
 * Pulls the hostname out of an absolute or protocol-relative url.
 * @param {string} url
 * @returns {string | undefined} lowercased hostname, undefined if unparseable
 */
export const getReturnUrlHost = (url) => {
  try {
    const { hostname } = new URL(url, PARSE_BASE);
    return hostname.toLowerCase();
  } catch {
    return undefined;
  }
};

/**
 * A path-only return (`/sso/callback`) stays inside this app, so there is no
 * host to check. `//host/path` is NOT path-only - it is protocol-relative.
 * @param {string} url
 * @returns {boolean}
 */
export const isLocalReturnUrl = (url) =>
  url.startsWith('/') && !url.startsWith('//');

/**
 * @param {Object} wsConfig
 * @returns {string[]} domains to match, config values included so each
 * environment allows its own hosts (`lego`, `127.0.0.1`, `localhost` locally)
 * without an env-specific branch here
 */
export const getAllowedReturnDomains = (wsConfig = {}) => {
  const {
    ccmsDomain,
    accountsDomain,
    onboardingDomain,
    parentWebUrl,
    defaultReturn,
    allowedReturnDomains = [],
  } = wsConfig;

  const fromConfig = [
    ccmsDomain,
    accountsDomain,
    onboardingDomain,
    parentWebUrl,
    defaultReturn,
  ]
    .filter(Boolean)
    .map(getReturnUrlHost)
    .filter(Boolean);

  return [...STATIC_ALLOWED_DOMAINS, ...allowedReturnDomains, ...fromConfig];
};

/**
 * @param {string | undefined} url return url from the hash payload
 * @param {Object} wsConfig
 * @returns {boolean} true when the url is safe to redirect to. An empty url is
 * allowed - the caller sent none and the default return url is used instead.
 */
export const isAllowedReturnUrl = (url, wsConfig = {}) => {
  if (!url) {
    return true;
  }
  if (isLocalReturnUrl(url)) {
    return true;
  }

  const host = getReturnUrlHost(url);
  if (!host) {
    return false;
  }

  return getAllowedReturnDomains(wsConfig).some(
    (domain) => host === domain || host.endsWith(`.${domain}`)
  );
};
